import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell, PublicShell } from '@/components/layout/AppShell';
import { ErrorBoundaryPage } from '@/pages/ErrorBoundary';
import { NotFound } from '@/pages/NotFound';
import { Landing } from '@/pages/Landing';
import { Login } from '@/pages/Login';
import { Showcase } from '@/pages/Showcase';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { S } from '@/lib/suspense';

// =============================================================================
// Lazy surfaces — every authenticated + every preview route is chunk-split.
// Cold visitors landing on / or /showcase pay nothing extra for them.
// =============================================================================

const TrainerDashboard = lazy(() =>
  import('@/features/trainer/TrainerDashboard').then((m) => ({ default: m.TrainerDashboard })),
);
const TrainerRoster = lazy(() =>
  import('@/features/trainer/TrainerRoster').then((m) => ({ default: m.TrainerRoster })),
);
const ClientDetail = lazy(() =>
  import('@/features/trainer/ClientDetail').then((m) => ({ default: m.ClientDetail })),
);
const ClientInvite = lazy(() =>
  import('@/features/trainer/ClientInvite').then((m) => ({ default: m.ClientInvite })),
);
const PlanList = lazy(() =>
  import('@/features/plans/PlanList').then((m) => ({ default: m.PlanList })),
);
const PlanEditor = lazy(() =>
  import('@/features/plans/PlanEditor').then((m) => ({ default: m.PlanEditor })),
);
const ExerciseLibrary = lazy(() =>
  import('@/features/exercises/ExerciseLibrary').then((m) => ({ default: m.ExerciseLibrary })),
);
const TrainerCalendar = lazy(() =>
  import('@/features/trainer/TrainerCalendar').then((m) => ({ default: m.TrainerCalendar })),
);
const TrainerAssistant = lazy(() =>
  import('@/features/assistant/TrainerAssistant').then((m) => ({ default: m.TrainerAssistant })),
);
const TrainerSettings = lazy(() =>
  import('@/features/trainer/TrainerSettings').then((m) => ({ default: m.TrainerSettings })),
);
const ClientPortal = lazy(() =>
  import('@/features/client/ClientPortal').then((m) => ({ default: m.ClientPortal })),
);
const WorkoutSession = lazy(() =>
  import('@/features/workouts/WorkoutSession').then((m) => ({ default: m.WorkoutSession })),
);
const ClientMeasurements = lazy(() =>
  import('@/features/measurements/ClientMeasurements').then((m) => ({
    default: m.ClientMeasurements,
  })),
);
const ClientProfile = lazy(() =>
  import('@/features/client/ClientProfile').then((m) => ({ default: m.ClientProfile })),
);
const IntakeForm = lazy(() =>
  import('@/features/intake/IntakeForm').then((m) => ({ default: m.IntakeForm })),
);

// Single-tenant build (current scope): one trainer is provisioned via SQL seed.
// Public signup / pricing / billing pages exist on disk but are deliberately
// not routed. Reinstate them when the product opens to multiple trainers.

const routes: RouteObject[] = [
  {
    element: <PublicShell />,
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/login', element: <Login /> },
      { path: '/intake/:token', element: S(<IntakeForm />) },
      { path: '/showcase', element: <Showcase /> },
      { path: '/preview/workouts', element: S(<WorkoutSession />) },
      { path: '/preview/roster', element: S(<TrainerRoster />) },
      { path: '/preview/plan-editor', element: S(<PlanEditor />) },
      { path: '/preview/measurements', element: S(<ClientMeasurements />) },
      { path: '/preview/dashboard', element: S(<TrainerDashboard />) },
      {
        path: '/preview/client-detail',
        element: <Navigate to="/preview/client-detail/c-001" replace />,
      },
      { path: '/preview/client-detail/:id', element: S(<ClientDetail />) },
      { path: '/preview/calendar', element: S(<TrainerCalendar />) },
      { path: '/preview/assistant', element: S(<TrainerAssistant />) },
      { path: '/preview/client-portal', element: S(<ClientPortal />) },
    ],
  },
  {
    element: (
      <ProtectedRoute role="trainer">
        <AppShell role="trainer" />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: '/trainer', element: S(<TrainerDashboard />) },
      { path: '/trainer/clients', element: S(<TrainerRoster />) },
      { path: '/trainer/clients/invite', element: S(<ClientInvite />) },
      { path: '/trainer/clients/:id', element: S(<ClientDetail />) },
      { path: '/trainer/plans', element: S(<PlanList />) },
      { path: '/trainer/plans/new', element: S(<PlanEditor />) },
      { path: '/trainer/plans/:planId', element: S(<PlanEditor />) },
      { path: '/trainer/exercises', element: S(<ExerciseLibrary />) },
      { path: '/trainer/calendar', element: S(<TrainerCalendar />) },
      { path: '/trainer/assistant', element: S(<TrainerAssistant />) },
      { path: '/trainer/settings', element: S(<TrainerSettings />) },
    ],
  },
  {
    element: (
      <ProtectedRoute role="client">
        <AppShell role="client" />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundaryPage />,
    children: [
      { path: '/client', element: S(<ClientPortal />) },
      { path: '/client/workouts', element: S(<WorkoutSession />) },
      { path: '/client/workouts/:planDayId', element: S(<WorkoutSession />) },
      { path: '/client/progress', element: S(<ClientMeasurements />) },
      { path: '/client/profile', element: S(<ClientProfile />) },
    ],
  },
  { path: '/app', element: <Navigate to="/trainer" replace /> },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes, {
  future: {
    // Data-router-level v7 future flags. Silences warnings + smooths upgrade.
    // v7_startTransition lives on RouterProvider — see main.tsx.
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
